import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
// react admin
import {
  Datagrid,
  DateField,
  List,
  TextField,
  TopToolbar,
  FunctionField,
  ReferenceField,
  useGetList,
  useUpdate,
  useRecordContext,
  useRefresh,
  useListContext,
  useListController,
  NumberField,
} from "react-admin";
// mui
import {
  Switch,
  Box,
  Button,
  Menu,
  MenuItem,
  TextField as MuiTextField,
  ListItemIcon,
  Chip,
  Typography,
  Select,
  InputLabel,
  FormControl,
  useTheme,
  useMediaQuery,
} from "@mui/material";
// mui icon
import GetAppIcon from "@mui/icons-material/GetApp";
import BackupTableIcon from "@mui/icons-material/BackupTable";
// component
import CustomPagination from "../../Components/CustomPagination";
import { NewGameFormDialog } from "./forms/NewGameForm";
import { EditGameFormDialog } from "./forms/EditGameForm";
import { DeleteGameRecords } from "./dialogs/DeleteGameRecords";
import { CreateDialogButton } from "../../Components/CreateDialogueButton";
import { ActionsMenu, ActionsMenuItem } from "../../Components/ActionsMenu";
import { ComingSoonDialog } from "./dialogs/ComingSoonDialog";
// loader
import { Loader } from "../Loader";
// utils
import { gameRecordsExports } from "../../Utilities/allExports";
// xlsx export
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const CustomFilter = () => {
  const { setFilters } = useListContext();

  const [gameVariant, setGameVariant] = useState("All");
  const [gameName, setGameName] = useState("");
  const [buildingId, setBuildingId] = useState("All");

  const { data: buildingData } = useGetList(
    "VegasBuilding",
    {
      pagination: { page: 1, perPage: 10 },
      sort: { field: "createdAt", order: "ASC" },
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const handleApply = () => {
    const filters = {};
    if (gameVariant !== "All") filters.type = gameVariant;
    if (buildingId !== "All") filters.building = buildingId;
    if (gameName) filters.name = gameName;
    setFilters(filters, {});
  };

  const handleClear = () => {
    setGameVariant("All");
    setBuildingId("All");
    setGameName("");
    setFilters({}, {});
  };

  return (
    <Box
      display={{ xs: "block", width: "100%", sm: "flex" }}
      alignItems="center"
      gap={2}
    >
      <Box display="flex" width="max-content">
        <MuiTextField
          size="small"
          variant="outlined"
          value={gameName}
          onChange={(e) => setGameName(e.target.value)}
          placeholder="Game Name"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleApply();
            }
          }}
        />

        <FormControl>
          <InputLabel>Game</InputLabel>
          <Select
            size="small"
            value={gameVariant}
            label="Game"
            onChange={(e) => setGameVariant(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="VE">VE</MenuItem>
            <MenuItem value="TD">TD</MenuItem>
          </Select>
        </FormControl>

        <FormControl>
          <InputLabel>Building</InputLabel>
          <Select
            size="small"
            value={buildingId}
            label="Building"
            onChange={(e) => setBuildingId(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="All">All</MenuItem>
            {buildingData?.map((ele) => (
              <MenuItem key={ele.id} value={ele.id}>
                {ele.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box display="flex" width="max-content">
        <Button
          variant="contained"
          sx={{
            marginTop: "10px",
            marginBottom: "10px",
            marginRight: "10px",
            background: "#FFC107",
            color: "#0A0A0A",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#FFA000",
            },
          }}
          onClick={handleApply}
        >
          Apply
        </Button>
        <Button
          variant="outlined"
          sx={{
            marginTop: "10px",
            marginBottom: "10px",
            marginRight: "10px",
            outline: "1px solid #FFC107",
            color: "white",
            textTransform: "none",
            border: "none",
          }}
          onClick={handleClear}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
};

const BooleanToggle = ({ source, onComingSoonToggle }) => {
  const refresh = useRefresh();
  const record = useRecordContext();
  const [update, { isLoading }] = useUpdate();

  if (!record) return null;

  const handleToggle = () => {
    // If it's the Coming Soon switch, open dialog instead
    if (source === "comingSoon" && onComingSoonToggle) {
      onComingSoonToggle(record, !record[source]);
      return;
    }

    // For other switches, update directly
    update(
      "gameCatalogueSwitch",
      {
        id: record.id,
        data: { [source]: !record[source] },
      },
      {
        onSuccess: () => refresh(),
      }
    );
  };

  return (
    <React.Fragment>
      <Switch
        checked={record[source]}
        onChange={handleToggle}
        disabled={isLoading}
      />
      {isLoading && <Loader />}
    </React.Fragment>
  );
};

export const GameRecordList = () => {
  const { isLoading, total, page, perPage, setPage, setPerPage } =
    useListController();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [isExporting, setIsExporting] = useState(false);
  const [comingSoonDialog, setComingSoonDialog] = useState({
    open: false,
    record: null,
    isEnabling: false,
  });

  const navigate = useNavigate();

  const currentUserData = localStorage.getItem(
    `Parse/${process.env.REACT_APP_APP_ID}/currentUser`
  );

  if (!currentUserData) {
    navigate("/login");
  }

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [filters, setFilters] = useState({});

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleComingSoonToggle = (record, isEnabling) => {
    setComingSoonDialog({
      open: true,
      record,
      isEnabling,
    });
  };

  const handleComingSoonDialogClose = () => {
    setComingSoonDialog({
      open: false,
      record: null,
      isEnabling: false,
    });
  };

  // export the data in xls formate
  const handleExportXLS = async () => {
    try {
      setIsExporting(true);

      const exportData = await gameRecordsExports(filters);

      if (!exportData || !exportData?.data || exportData?.data?.length === 0) {
        console.warn("No data to export.");
        return;
      }

      const selectedFields = exportData.data.map((item) => ({
        Name: item.name,
        Active: item.isActive,
        "Coming Soon": item.comingSoon,
        Category: item.category,
        Description: item.description,
        Date: new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false, // Set to true for AM/PM format
        }).format(new Date(item.createdAt)),
      }));

      const worksheet = XLSX.utils.json_to_sheet(selectedFields);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Game Records");
      const xlsData = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([xlsData], { type: "application/octet-stream" }),
        "GameRecords.xlsx"
      );
    } catch (error) {
      console.error("Error fetching user engagement:", error);
    } finally {
      setIsExporting(false);
      handleMenuClose();
    }
  };

  const listActions = (
    <TopToolbar>
      <CreateDialogButton>
        Create New Game
        <NewGameFormDialog />
      </CreateDialogButton>
      <Button
        variant="contained"
        startIcon={<GetAppIcon />}
        sx={{
          marginLeft: "10px",
          background: "#FFC107",
          color: "#0A0A0A",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#FFA000",
          },
        }}
        onClick={handleMenuOpen}
      >
        Export
      </Button>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        sx={{ marginTop: 1 }}
      >
        {/* <MenuItem onClick={handleExportPDF}>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            PDF file
          </Typography>
        </MenuItem> */}

        <MenuItem onClick={handleExportXLS} disabled={isExporting}>
          <ListItemIcon>
            <BackupTableIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {isExporting ? "Exporting..." : "Excel file"}
          </Typography>
        </MenuItem>
      </Menu>
    </TopToolbar>
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          mt: 4,
          backgroundColor: "#242424",
          borderRadius: 2,
          boxShadow: "none !important",
          padding: isSmallScreen ? { xs: 0, sm: 0 } : { xs: 1.5, sm: 2 },
        }}
      >
        <List
          title="Game Records"
          filters={<CustomFilter />}
          actions={listActions}
          pagination={false}
          sort={{ field: "createdAt", order: "DESC" }}
          sx={{
            "& .RaList-actions": {
              flexWrap: "nowrap",
            },
            "& .RaFilterFormInput-spacer": { display: "none" },
          }}
        >
          <Box
            style={{
              width: "100%",
              overflowX: "auto",
            }}
          >
            <Box
              style={{
                width: "100%",
                position: isSmallScreen ? "absolute" : "relative",
              }}
            >
              <Datagrid
                bulkActionButtons={false}
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  width: "100%",
                  maxHeight: "100%",
                  "& .RaDatagrid-table": {
                    width: "100%",
                  },
                }}
              >
                <ActionsMenu label="Actions">
                  <ActionsMenuItem>
                    Edit
                    <EditGameFormDialog />
                  </ActionsMenuItem>
                  <ActionsMenuItem>
                    Delete
                    <DeleteGameRecords />
                  </ActionsMenuItem>
                </ActionsMenu>
                <BooleanToggle source="isActive" label="Active" />
                <BooleanToggle source="isNewGame" label="New Game" />
                <BooleanToggle 
                  source="comingSoon" 
                  label="Coming Soon" 
                  onComingSoonToggle={handleComingSoonToggle}
                />
                <TextField source="name" label="Game Name" />
                <FunctionField
                  label="Game Variant"
                  source="type"
                  render={(record) => {
                    const getColor = (status) => {
                      switch (status) {
                        case "TD":
                          return "secondary";
                        case "VE":
                          return "primary";
                        default:
                          return "default";
                      }
                    };
                    return (
                      <Chip
                        label={record.type}
                        color={getColor(record.type)}
                        size="small"
                        variant="outlined"
                      />
                    );
                  }}
                />
                <TextField source="category" label="Category" />
                <ReferenceField
                  source="building.id"
                  reference="GameCatalogue"
                  label="Vegas Building"
                  emptyText="Missing Building"
                  link={false}
                  sortBy="building.name"
                >
                  <TextField source="name" />
                </ReferenceField>
                <NumberField
                  source="gameSequence"
                  label="Game Sequence"
                  textAlign="left"
                />
                <DateField source="createdAt" label="Recording Time" showTime />
              </Datagrid>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100% !important",
                  margin: "16px 0px",
                }}
              >
                <CustomPagination
                  page={page}
                  perPage={perPage}
                  total={total}
                  setPage={setPage}
                  setPerPage={setPerPage}
                />
              </Box>
            </Box>
          </Box>
        </List>
      </Box>
      
      <ComingSoonDialog
        open={comingSoonDialog.open}
        onClose={handleComingSoonDialogClose}
        record={comingSoonDialog.record}
        isEnabling={comingSoonDialog.isEnabling}
      />
    </React.Fragment>
  );
};
